import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useStudio } from "@/context/StudioContext";
import { PageHeader, Panel, Loading, Tag, StatusBadge } from "@/components/common";
import { Crosshair as Radar, Sparkle, Spinner, Warning, Eye, BookmarkSimple, XCircle, GameController, FilmSlate } from "@phosphor-icons/react";
import { toast } from "sonner";

const STAGE_COLORS = { EARLY_SIGNAL: "#00E5FF", RISING: "#00FF9D", MAINSTREAM: "#FFB800", SATURATED: "#FF9500", DECLINING: "#FF3366" };
const FILTERS = ["all", "new", "watch", "saved"];

export default function HypeRadar() {
  const { active } = useStudio();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [domain, setDomain] = useState("kids animation & mobile games");
  const [region, setRegion] = useState("Global");
  const [filter, setFilter] = useState("all");
  const [sources, setSources] = useState(null);

  const load = () => { api.get("/trends").then(({ data }) => setItems(data)); api.get("/trends/sources").then(({ data }) => setSources(data)).catch(() => {}); };
  useEffect(() => { load(); }, [active?.id]);

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/trends/scan", { domain, region });
      const started = Date.now();
      const poll = async () => {
        try {
          const { data: job } = await api.get(`/trends/scan-job/${data.job_id}`);
          if (job.status === "done") { toast.success(`Found ${job.count} opportunities`); load(); setScanning(false); }
          else if (job.status === "error") { toast.error("Scan failed"); setScanning(false); }
          else if (Date.now() - started > 150000) { toast.error("Scan timed out"); setScanning(false); }
          else setTimeout(poll, 3000);
        } catch { toast.error("Scan failed"); setScanning(false); }
      };
      setTimeout(poll, 3000);
    } catch (e) { toast.error(e.response?.data?.detail || "Scan failed"); setScanning(false); }
  };
  const setStatus = async (t, status) => { await api.patch(`/trends/${t.id}`, { status }); load(); };
  const remove = async (t) => { await api.delete(`/trends/${t.id}`); load(); };
  const spawn = async (t, target) => {
    try {
      const { data } = await api.post(`/trends/${t.id}/spawn`, { target });
      toast.success(target === "game" ? "Game concept created" : "Content brief created");
      load();
      navigate(data.route);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!items) return <Loading />;
  const shown = filter === "all" ? items : items.filter((t) => t.status === filter);

  return (
    <div>
      <PageHeader icon={Radar} title="Hype Radar — Trend Intelligence"
        subtitle="AI-surfaced original opportunities for content & games. Turn them into briefs — but always originate, never clone."
        actions={<button data-testid="scan-trends" onClick={scan} disabled={scanning} className="inline-flex items-center gap-2 rounded-full bg-magic px-4 py-2 text-sm font-semibold text-black disabled:opacity-60">{scanning ? <Spinner size={16} className="animate-spin" /> : <Sparkle size={16} weight="fill" />} {scanning ? "Scanning…" : "Scan Trends"}</button>} />

      <div data-testid="prediction-banner" className="mb-5 flex items-start gap-3 rounded-xl border border-[#FFB800]/40 bg-[#FFB800]/10 px-4 py-3">
        <Warning size={20} weight="fill" className="text-[#FFB800] shrink-0 mt-0.5" />
        <p className="text-sm text-[#FFE08A]"><b>AI PREDICTION — not measured market data.</b> These are informed hypotheses from the Trend Research agent, not live signals from Google Trends / Steam / App Store. Connect real data sources for measured trends. Use as inspiration only and run an originality check before producing.</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mb-5">
        <input data-testid="scan-domain" value={domain} onChange={(e) => setDomain(e.target.value)} className="flex-1 rounded-xl bg-black/30 border border-white/10 px-4 py-2.5 text-white outline-none" placeholder="Domain (e.g. preschool learning games)" />
        <input data-testid="scan-region" value={region} onChange={(e) => setRegion(e.target.value)} className="sm:w-48 rounded-xl bg-black/30 border border-white/10 px-4 py-2.5 text-white outline-none" placeholder="Region" />
      </div>

      {sources && (
        <div data-testid="trend-sources" className="mb-5 rounded-xl border border-white/10 bg-black/20 px-4 py-3">
          <div className="text-xs text-zinc-400 mb-2">Measured data sources — <b className="text-zinc-300">connect in Setup Center to add real signals</b></div>
          <div className="flex flex-wrap gap-2">
            {sources.sources.map((s) => (
              <span key={s.id} data-testid={`source-${s.id}`} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ${s.status === "CONNECTED" ? "border-[#00FF9D]/40 text-[#00FF9D]" : "border-white/10 text-zinc-500"}`}>
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.status === "CONNECTED" ? "#00FF9D" : "#666" }} />
                {s.name.split(" ")[0]} · {s.status === "CONNECTED" ? "CONNECTED" : "NOT CONNECTED"}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-4">
        {FILTERS.map((f) => <button key={f} data-testid={`trend-filter-${f}`} onClick={() => setFilter(f)} className={`rounded-full px-3 py-1.5 text-sm border capitalize ${filter === f ? "border-[#00E5FF]/50 bg-[#00E5FF]/10 text-[#00E5FF]" : "border-white/10 text-zinc-500"}`}>{f}</button>)}
      </div>

      {shown.length === 0 ? <p className="text-sm text-zinc-500">No trends yet. Click <b>Scan Trends</b> to have the Trend Research agent surface original opportunities.</p> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {shown.map((t) => (
            <Panel key={t.id} data-testid={`trend-${t.id}`} className="flex flex-col">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ color: STAGE_COLORS[t.stage], background: `${STAGE_COLORS[t.stage]}1a` }}>{t.stage.replace(/_/g, " ")}</span>
                <Tag className="text-[10px] capitalize">{t.category}</Tag>
              </div>
              <h4 className="font-display text-base font-bold text-white">{t.topic}</h4>
              <div className="flex items-center gap-3 my-2">
                <Ring label="Score" v={t.scores?.trend_score} />
                <Ring label="Opportunity" v={t.scores?.opportunity} />
                <Ring label="Confidence" v={t.scores?.confidence} />
              </div>
              {t.why_now && <p className="text-xs text-zinc-400 line-clamp-2"><b className="text-zinc-300">Why now:</b> {t.why_now}</p>}
              {t.content_angle && <p className="text-xs text-zinc-400 mt-1 flex gap-1"><FilmSlate size={13} className="text-[#00E5FF] shrink-0 mt-0.5" /><span className="line-clamp-2">{t.content_angle}</span></p>}
              {t.game_angle && <p className="text-xs text-zinc-400 mt-1 flex gap-1"><GameController size={13} className="text-[#FF007F] shrink-0 mt-0.5" /><span className="line-clamp-2">{t.game_angle}</span></p>}
              {t.scores?.originality_risk != null && <p className="text-[10px] mt-2 text-zinc-500">Originality risk: <span className={t.scores.originality_risk > 60 ? "text-[#FF3366]" : "text-[#00FF9D]"}>{t.scores.originality_risk}/100</span></p>}
              <div className="mt-2 flex gap-1.5">
                <button data-testid={`spawn-content-${t.id}`} onClick={() => spawn(t, "content")} className="flex-1 inline-flex items-center justify-center gap-1 rounded-full bg-[#00E5FF]/15 border border-[#00E5FF]/40 text-[#00E5FF] px-2.5 py-1.5 text-xs font-semibold"><FilmSlate size={13} /> Content Brief</button>
                <button data-testid={`spawn-game-${t.id}`} onClick={() => spawn(t, "game")} className="flex-1 inline-flex items-center justify-center gap-1 rounded-full bg-[#FF007F]/15 border border-[#FF007F]/40 text-[#FF007F] px-2.5 py-1.5 text-xs font-semibold"><GameController size={13} /> Game Concept</button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button data-testid={`watch-${t.id}`} onClick={() => setStatus(t, "watch")} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs ${t.status === "watch" ? "border-[#00E5FF] text-[#00E5FF]" : "border-white/10 text-zinc-400"}`}><Eye size={12} /> Watch</button>
                <button data-testid={`save-${t.id}`} onClick={() => setStatus(t, "saved")} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs ${t.status === "saved" ? "border-[#00FF9D] text-[#00FF9D]" : "border-white/10 text-zinc-400"}`}><BookmarkSimple size={12} /> Save</button>
                <button data-testid={`ignore-${t.id}`} onClick={() => setStatus(t, "ignored")} className="inline-flex items-center gap-1 rounded-full border border-white/10 text-zinc-500 px-2.5 py-1 text-xs"><XCircle size={12} /> Ignore</button>
                <button data-testid={`del-trend-${t.id}`} onClick={() => remove(t)} className="ml-auto text-zinc-600 hover:text-[#FF3366] text-xs">Delete</button>
              </div>
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}

function Ring({ label, v }) {
  const val = Number(v || 0);
  return (
    <div className="flex flex-col items-center">
      <div className="relative h-10 w-10">
        <svg viewBox="0 0 36 36" className="h-10 w-10 -rotate-90">
          <circle cx="18" cy="18" r="15" fill="none" stroke="#ffffff14" strokeWidth="4" />
          <circle cx="18" cy="18" r="15" fill="none" stroke="#00E5FF" strokeWidth="4" strokeDasharray={`${(val / 100) * 94.2} 94.2`} strokeLinecap="round" />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold text-white">{val}</span>
      </div>
      <span className="text-[9px] text-zinc-500 mt-0.5">{label}</span>
    </div>
  );
}
